import { useState } from 'react'
import merge from 'lodash/merge'
import { noValidation } from '../validate/validate'

type FormValue<T> = {
  value: T
  valid: boolean
  message: string
}

export interface FormValues {
  [key: string]: FormValue<unknown>
}

export type Form<Values> = {
  [K in keyof Values]?: FormValue<Values[K]>
}

export type FormAsValues<Values> = {
  [K in keyof Values]: Values[K]
}

export type InputAttrs<T> = {
  value: T
  onChange: (value: T) => void
  isTouched: boolean
  isValid: boolean
  validationMessage: string
  label?: string
  options?: any
}

export type FormState<Values> = {
  [K in keyof Values]: InputAttrs<Values[K]>
}

export type Validator<T> = (value: T, data: Record<string, unknown>) => [boolean, string]

function reduceToValues<Values = FormValues>(form: Form<Values>) {
  const values = Object.entries(form).reduce(
    (previous, [key, data]) => ({
      ...previous,
      [key]: (data as any).value,
    }),
    {} as FormAsValues<Values>,
  )

  return values
}

function useInput<T>(data: FormValue<T>, updateData: (value: T) => void): InputAttrs<T> {
  const [isTouched, setIsTouched] = useState(false)

  const onChange = (newValue: T) => {
    if (!isTouched) {
      setIsTouched(true)
    }
    updateData(newValue)
  }

  return {
    value: data.value,
    isValid: data.valid,
    validationMessage: data.message,
    onChange,
    isTouched,
  }
}

export type UseFormType = {
  formState: Function
  getFormValues: Function
  setFormData: Function
  inputState?: Function
  validate?: Function
  isValid?: boolean
}

export function useForm<Values = FormValues>(
  initialValues: Values,
  validators: Partial<Record<keyof Values, Validator<Values[keyof Values]>>> = {},
): UseFormType {
  const getValidator = (key: any) => validators[key] || noValidation
  const getFormValues = () => reduceToValues<Values>(data)

  const dataToFormData = (data: Values): Form<Values> =>
    Object.entries(data).reduce((acc, [key, initialValue]) => {
      const [valid] = getValidator(key)(initialValue, data)
      acc[key] = {
        value: initialValue,
        message: '',
        valid,
      }
      return acc
    }, {})

  const [data, setData] = useState<Form<Values>>(
    Object.entries(initialValues).reduce((acc, [key, initialValue]) => {
      const [valid] = getValidator(key)(initialValue, initialValues)
      acc[key] = {
        value: initialValue,
        message: '',
        valid,
      }
      return acc
    }, {}),
  )

  // Set form data for multiple or all form fields simultaneously
  const setFormData = (data: Values): void => {
    const formData = dataToFormData(merge({}, initialValues, data))

    setData(formData)
  }

  const [isFormValid, setIsFormValid] = useState(false)

  // Update form field data
  const updateData = (key: keyof Values, value: Values[keyof Values]): void => {
    const [valid, message] = getValidator(key)(value, reduceToValues<Values>(data))
    setData({
      ...data,
      [key]: {
        message,
        valid,
        value,
      },
    })
  }

  // Validate all form fields using the supplied validator functions
  const validateForm = (): boolean => {
    const validatedFormData = Object.keys(validators).reduce(
      (acc, key) => {
        const [valid, message] = getValidator(key)(data[key].value, reduceToValues<Values>(data))
        acc[key] = {
          ...data[key],
          message,
          valid,
        }

        return acc
      },
      { ...data },
    )

    setData(validatedFormData)
    const isFormValid = Object.keys(validatedFormData).every(key => validatedFormData[key].valid)
    setIsFormValid(isFormValid)

    return isFormValid
  }

  // Returns state for a single input
  function inputState<T extends keyof Values>(key: T) {
    return useInput(data[key], (value: Values[keyof Values]) => updateData(key, value))
  }

  // Returns state for all fields in the form
  function formState() {
    return Object.keys(data).reduce(
      (previous: FormState<Values>, current: any) => ({
        ...previous,
        [current]: useInput(data[current], (value: Values[keyof Values]) => updateData(current, value)),
      }),
      {} as FormState<Values>,
    )
  }

  return {
    inputState,
    formState,
    getFormValues,
    setFormData,
    validate: validateForm,
    isValid: isFormValid,
  }
}
